import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Marquee from "react-fast-marquee";
import {
  Sparkles, Wand2, ArrowRight, Mail, Bot, FileText, Image, Mic, Code,
  Newspaper, Globe, Shield, Zap, Star, Rocket, Heart, BookOpen,
  GraduationCap, Target, Cpu, Menu, XCircle, MessageSquare, Users,
  Calendar, Award, Briefcase, ShoppingBag, Play, Send, Smile, ImageIcon,
  Hash, Bell, PlusCircle, Phone, Video,
  Linkedin, Twitter, Instagram, Youtube, Facebook, Github, ExternalLink, PlayCircle
} from "lucide-react";
import { startDemoTour } from "../components/DemoAutoTour";

const SOCIAL_ICON_MAP = {
  linkedin: Linkedin, twitter: Twitter, instagram: Instagram,
  youtube: Youtube, facebook: Facebook, github: Github
};
const GV2_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const css = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800;900&display=swap');
@keyframes morph{0%,100%{border-radius:60% 40% 30% 70%/60% 30% 70% 40%}50%{border-radius:30% 60% 70% 40%/50% 60% 30% 60%}}
@keyframes orbit{0%{transform:rotate(0deg) translateX(130px) rotate(0deg)}100%{transform:rotate(360deg) translateX(130px) rotate(-360deg)}}
@keyframes orbit2{0%{transform:rotate(180deg) translateX(180px) rotate(-180deg)}100%{transform:rotate(540deg) translateX(180px) rotate(-540deg)}}
@keyframes spin-slow{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}
@keyframes float-g1{0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(30px,-40px) scale(1.1)}}
@keyframes float-g2{0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(-20px,30px) scale(0.95)}}
@keyframes float-g3{0%,100%{transform:translate(0,0)}50%{transform:translate(15px,20px)}}
@keyframes pulse-node-v2{0%,100%{opacity:0.3}50%{opacity:0.8}}
@keyframes shoot-v2{0%{offset-distance:0%;opacity:0}5%{opacity:1}90%{opacity:0.8}100%{offset-distance:100%;opacity:0}}
@keyframes wave-drift{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
@keyframes typing{0%{width:0}50%{width:100%}100%{width:100%}}
@keyframes blink{0%,100%{opacity:1}50%{opacity:0}}
@keyframes msg-appear{0%{opacity:0;transform:translateX(-20px)}100%{opacity:1;transform:translateX(0)}}
@keyframes react-pop{0%{transform:scale(0)}50%{transform:scale(1.3)}100%{transform:scale(1)}}
@keyframes notify-slide{0%{transform:translateX(100%);opacity:0}10%{transform:translateX(0);opacity:1}90%{transform:translateX(0);opacity:1}100%{transform:translateX(100%);opacity:0}}
@keyframes circuit-pulse{0%,100%{opacity:0.04}50%{opacity:0.1}}
.morph{animation:morph 8s ease-in-out infinite;will-change:border-radius}
.orbit{animation:orbit 18s linear infinite;will-change:transform}
.orbit2{animation:orbit2 24s linear infinite;will-change:transform}
.spin-slow{animation:spin-slow 30s linear infinite;will-change:transform}
.float-g1{animation:float-g1 7s ease-in-out infinite;will-change:transform}
.float-g2{animation:float-g2 9s ease-in-out infinite;will-change:transform}
.float-g3{animation:float-g3 5s ease-in-out infinite;will-change:transform}
.glass{background:rgba(255,255,255,0.05);backdrop-filter:blur(20px);border:1px solid rgba(255,255,255,0.08)}
.glass-strong{background:rgba(255,255,255,0.08);backdrop-filter:blur(30px);border:1px solid rgba(255,255,255,0.12)}
.typing-line{animation:typing 3s steps(40) infinite}
.cursor-blink{animation:blink 0.8s step-end infinite}
.msg-1{animation:msg-appear 0.5s ease-out 0.5s both}
.msg-2{animation:msg-appear 0.5s ease-out 1.5s both}
.msg-3{animation:msg-appear 0.5s ease-out 2.5s both}
.react-pop{animation:react-pop 0.4s ease-out 3s both}
.notify-anim{animation:notify-slide 4s ease-in-out infinite}
.wave-drift{animation:wave-drift 20s linear infinite;will-change:transform}
.circuit-pulse{animation:circuit-pulse 4s ease-in-out infinite}
`;

const LOGO = "/brain-logo.png?v=4";

/* ── Neural network SVG — optimized: 16 nodes, static lines, 4 shooting stars ── */
const NeuralBg = () => {
  const nodes = [
    {x:120,y:100},{x:400,y:60},{x:720,y:130},{x:1050,y:70},{x:1320,y:140},
    {x:200,y:280},{x:560,y:250},{x:840,y:310},{x:1140,y:260},
    {x:100,y:430},{x:440,y:400},{x:700,y:460},{x:1000,y:420},{x:1300,y:450},
    {x:300,y:560},{x:900,y:550},
  ];
  const colors = ["#8B5CF6", "#EC4899", "#3B82F6"];
  const connections = [];
  for (let i = 0; i < nodes.length; i++) {
    for (let j = i + 1; j < nodes.length; j++) {
      const dx = nodes[i].x - nodes[j].x, dy = nodes[i].y - nodes[j].y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 320) connections.push([i, j, dist < 180 ? 0.25 : 0.1]);
    }
  }
  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 1440 700" preserveAspectRatio="xMidYMid slice">
      <defs>
        <linearGradient id="line-grad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#8B5CF6" />
          <stop offset="50%" stopColor="#EC4899" />
          <stop offset="100%" stopColor="#3B82F6" />
        </linearGradient>
        <radialGradient id="star-glow">
          <stop offset="0%" stopColor="#fff" stopOpacity="1" />
          <stop offset="40%" stopColor="#8B5CF6" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0" />
        </radialGradient>
      </defs>
      {/* Static connection lines — no per-line animation */}
      {connections.map(([a, b, op], i) => (
        <line key={i} x1={nodes[a].x} y1={nodes[a].y} x2={nodes[b].x} y2={nodes[b].y}
          stroke="url(#line-grad)" strokeWidth="0.7" opacity={op} />
      ))}
      {/* Nodes — single circle, gentle pulse */}
      {nodes.map((n, i) => (
        <circle key={i} cx={n.x} cy={n.y} r="3" fill={colors[i % 3]}
          opacity="0.5" style={{ animation: `pulse-node-v2 ${3 + (i % 2)}s ease-in-out infinite`, animationDelay: `${(i * 0.6) % 4}s`, willChange: "opacity" }} />
      ))}
      {/* 4 shooting stars instead of 50+ */}
      {[
        {x1:50,y1:120,x2:550,y2:350,d:12},
        {x1:600,y1:40,x2:1100,y2:280,d:15},
        {x1:900,y1:100,x2:1400,y2:400,d:13},
        {x1:200,y1:400,x2:750,y2:600,d:14},
      ].map((s, i) => (
        <circle key={`ss${i}`} r="3.5" fill="url(#star-glow)" style={{
          offsetPath: `path("M${s.x1},${s.y1} L${s.x2},${s.y2}")`,
          animation: `shoot-v2 ${s.d}s ease-in-out infinite`,
          animationDelay: `${i * 3.5}s`,
          willChange: "offset-distance, opacity",
        }} />
      ))}
    </svg>
  );
};

/* ── Circuit board traces ── */
const CircuitTraces = () => (
  <svg className="absolute inset-0 w-full h-full pointer-events-none circuit-pulse" viewBox="0 0 1440 700" preserveAspectRatio="xMidYMid slice">
    {/* Horizontal traces */}
    <path d="M0 200 H400 L420 180 H600" stroke="#8B5CF6" strokeWidth="0.5" fill="none" opacity="0.06" />
    <path d="M800 350 H1000 L1020 370 H1200 L1220 350 H1440" stroke="#EC4899" strokeWidth="0.5" fill="none" opacity="0.05" />
    <path d="M0 500 H200 L220 480 H400 L420 500 H600" stroke="#3B82F6" strokeWidth="0.5" fill="none" opacity="0.04" />
    <path d="M700 150 H900 L920 170 H1100" stroke="#14B8A6" strokeWidth="0.5" fill="none" opacity="0.05" />
    {/* Junction dots */}
    {[[400,200],[600,200],[1000,350],[1200,350],[200,500],[400,500],[900,150],[1100,170]].map(([x,y],i) => (
      <circle key={i} cx={x} cy={y} r="3" fill="none" stroke={i%2===0?"#8B5CF6":"#EC4899"} strokeWidth="0.5" opacity="0.1" />
    ))}
  </svg>
);

/* ── Sine wave pulse lines ── */
const WaveLines = () => (
  <div className="absolute bottom-0 left-0 right-0 h-48 pointer-events-none overflow-hidden opacity-[0.04]">
    <svg className="w-[200%] h-full wave-drift" viewBox="0 0 2880 200" preserveAspectRatio="none">
      <path d="M0 100 Q180 20 360 100 Q540 180 720 100 Q900 20 1080 100 Q1260 180 1440 100 Q1620 20 1800 100 Q1980 180 2160 100 Q2340 20 2520 100 Q2700 180 2880 100" stroke="#8B5CF6" strokeWidth="1.5" fill="none" />
      <path d="M0 120 Q180 50 360 120 Q540 190 720 120 Q900 50 1080 120 Q1260 190 1440 120 Q1620 50 1800 120 Q1980 190 2160 120 Q2340 50 2520 120 Q2700 190 2880 120" stroke="#EC4899" strokeWidth="1" fill="none" />
    </svg>
  </div>
);

/* ── Gradient Blobs ── */
const Blobs = ({ v = 1 }) => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {v === 1 ? <>
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] morph float-g1 opacity-40" style={{ background: "radial-gradient(circle, rgba(139,92,246,0.4) 0%, transparent 70%)" }} />
      <div className="absolute -bottom-40 -left-40 w-[600px] h-[600px] morph float-g2 opacity-30" style={{ background: "radial-gradient(circle, rgba(236,72,153,0.35) 0%, transparent 70%)", animationDelay: "2s" }} />
      <div className="absolute top-1/3 right-1/4 w-[300px] h-[300px] morph float-g3 opacity-25" style={{ background: "radial-gradient(circle, rgba(59,130,246,0.3) 0%, transparent 70%)", animationDelay: "4s" }} />
    </> : <>
      <div className="absolute -top-20 -left-20 w-[400px] h-[400px] morph float-g2 opacity-30" style={{ background: "radial-gradient(circle, rgba(20,184,166,0.3) 0%, transparent 70%)" }} />
      <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] morph float-g1 opacity-25" style={{ background: "radial-gradient(circle, rgba(251,191,36,0.25) 0%, transparent 70%)", animationDelay: "3s" }} />
    </>}
  </div>
);

/* ── Post Mockup ── */
const PostMockup = () => (
  <div className="glass-strong rounded-2xl p-5 w-full max-w-md mx-auto relative" style={{ boxShadow: "0 20px 60px rgba(139,92,246,0.15)" }}>
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-pink-500" />
      <div><p className="text-white text-sm font-bold">Arjun Mehta</p><p className="text-gray-500 text-[10px]">Posting to AI Community</p></div>
    </div>
    <div className="bg-white/[0.03] rounded-xl p-4 mb-3 border border-white/5">
      <p className="text-gray-300 text-sm"><span className="typing-line inline-block overflow-hidden whitespace-nowrap">Just built my first RAG pipeline with Gemini 3!</span><span className="cursor-blink text-violet-400">|</span></p>
    </div>
    <div className="flex items-center justify-between">
      <div className="flex gap-3">
        <div className="w-8 h-8 rounded-lg bg-violet-500/10 flex items-center justify-center"><ImageIcon className="w-4 h-4 text-violet-400" /></div>
        <div className="w-8 h-8 rounded-lg bg-pink-500/10 flex items-center justify-center"><Smile className="w-4 h-4 text-pink-400" /></div>
        <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center"><Hash className="w-4 h-4 text-blue-400" /></div>
      </div>
      <button className="px-5 py-2 rounded-full text-xs font-bold text-white" style={{ background: "linear-gradient(135deg, #8B5CF6, #EC4899)" }}>Post <Send className="inline w-3 h-3 ml-1" /></button>
    </div>
    <div className="absolute -top-3 -right-3 notify-anim"><div className="bg-green-500 text-white text-[10px] font-bold px-3 py-1.5 rounded-full flex items-center gap-1 shadow-lg"><Bell className="w-3 h-3" /> 3 new reactions</div></div>
  </div>
);

/* ── Message Mockup ── */
const MessageMockup = () => (
  <div className="glass-strong rounded-2xl p-5 w-full max-w-md mx-auto" style={{ boxShadow: "0 20px 60px rgba(236,72,153,0.12)" }}>
    <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/5">
      <div className="flex items-center gap-3"><div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500" /><div><p className="text-white text-sm font-bold">Priya Sharma</p><p className="text-emerald-400 text-[10px] font-medium">Online</p></div></div>
      <div className="flex gap-2"><div className="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center"><Phone className="w-3.5 h-3.5 text-gray-500" /></div><div className="w-7 h-7 rounded-full bg-white/5 flex items-center justify-center"><Video className="w-3.5 h-3.5 text-gray-500" /></div></div>
    </div>
    <div className="space-y-3 mb-4">
      <div className="msg-1 flex gap-2 items-end"><div className="w-6 h-6 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 flex-shrink-0" /><div className="bg-white/[0.06] rounded-2xl rounded-bl-sm px-4 py-2.5 max-w-[75%]"><p className="text-gray-300 text-xs">Hey! Have you tried the new Claude integration?</p></div></div>
      <div className="msg-2 flex gap-2 items-end justify-end"><div className="rounded-2xl rounded-br-sm px-4 py-2.5 max-w-[75%]" style={{ background: "linear-gradient(135deg, #8B5CF6, #7C3AED)" }}><p className="text-white text-xs">Yes! It's amazing for code reviews</p></div></div>
      <div className="msg-3 flex gap-2 items-end"><div className="w-6 h-6 rounded-full bg-gradient-to-br from-emerald-400 to-cyan-500 flex-shrink-0" /><div className="bg-white/[0.06] rounded-2xl rounded-bl-sm px-4 py-2.5 max-w-[75%]"><p className="text-gray-300 text-xs">Let's pair program on the hackathon project!</p><div className="react-pop mt-1.5 inline-flex items-center gap-1 bg-white/5 rounded-full px-2 py-0.5"><span className="text-[10px]">&#x1F525;</span><span className="text-[10px] text-gray-400">2</span></div></div></div>
    </div>
    <div className="flex items-center gap-2"><div className="flex-1 bg-white/[0.04] rounded-full px-4 py-2 border border-white/5 flex items-center"><PlusCircle className="w-4 h-4 text-gray-500 mr-2" /><span className="text-gray-500 text-xs">Type a message...</span></div><div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "linear-gradient(135deg, #8B5CF6, #EC4899)" }}><Send className="w-3.5 h-3.5 text-white" /></div></div>
  </div>
);

const ThemeGlassV2 = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [socialLinks, setSocialLinks] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!document.getElementById("glassv2-css")) {
      const s = document.createElement("style"); s.id = "glassv2-css"; s.textContent = css; document.head.appendChild(s);
    }
    const fn = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    axios.get(`${GV2_API}/social-links`).then(r => setSocialLinks(r.data || [])).catch(() => {});
  }, []);

  const scrollTo = id => { document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }); setMobileMenu(false); };
  const navLinks = [
    { label: "Events", to: "/events" },
    { label: "Webinars", to: "/webinars" },
    { label: "Hackathons", to: "/hackathons" },
    { label: "AI News", to: "/ai-news" },
    { label: "Courses", to: "/ai-courses" },
    { label: "Jobs", to: "/ai-jobs" },
  ];
  const handleNav = (to) => { setMobileMenu(false); navigate(to); };

  return (
    <div className="min-h-screen text-white overflow-x-hidden" style={{ backgroundColor: "#0A0A1A", fontFamily: "'Outfit', sans-serif" }}>

      {/* NAV — links left, brain logo center, buttons right */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300" style={{ backgroundColor: scrolled ? "rgba(10,10,26,0.85)" : "transparent", backdropFilter: scrolled ? "blur(24px)" : "none", borderBottom: scrolled ? "1px solid rgba(255,255,255,0.06)" : "none" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 h-14 sm:h-16 flex items-center justify-between">
          {/* Left — Hamburger (mobile) / Nav links (desktop) */}
          <div className="flex items-center gap-1">
            <button className="lg:hidden text-gray-400 hover:text-white p-1" onClick={() => setMobileMenu(!mobileMenu)} data-testid="mobile-menu-btn">
              {mobileMenu ? <XCircle className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map(l => <button key={l.label} onClick={() => handleNav(l.to)} className="px-3 py-1.5 text-sm font-semibold rounded-full transition-all hover:bg-white/5 hover:text-white" style={{ color: "#9CA3AF" }} data-testid={`home-nav-${l.label.toLowerCase().replace(/ /g, "-")}`}>{l.label}</button>)}
            </div>
          </div>
          {/* Right — Log In + Sign Up */}
          <div className="flex items-center gap-2">
            <button className="text-xs sm:text-sm font-semibold px-3 sm:px-5 py-1.5 sm:py-2 rounded-full transition-colors hover:text-white border border-white/10" style={{ color: "#9CA3AF" }} onClick={() => navigate("/login")} data-testid="nav-login">Log In</button>
            <button className="text-xs sm:text-sm font-bold text-white px-3 sm:px-6 py-1.5 sm:py-2.5 rounded-full transition-all hover:-translate-y-0.5" style={{ background: "linear-gradient(90deg, #8B5CF6, #EC4899, #F59E0B)", boxShadow: "0 4px 20px rgba(139,92,246,0.3)" }} onClick={() => navigate("/register")} data-testid="nav-signup">
              <span className="hidden sm:inline">Get Started Free</span>
              <span className="sm:hidden">Sign Up</span>
            </button>
          </div>
        </div>
        {/* Mobile menu dropdown */}
        {mobileMenu && (
          <div className="lg:hidden px-6 pb-4" style={{ backgroundColor: "rgba(10,10,26,0.95)", backdropFilter: "blur(24px)" }}>
            <div className="flex flex-col gap-1 pt-2">
              {navLinks.map(l => (
                <button key={l.label} onClick={() => handleNav(l.to)} className="text-left px-4 py-2.5 text-sm font-semibold rounded-lg transition-all hover:bg-white/5 hover:text-white" style={{ color: "#9CA3AF" }}>{l.label}</button>
              ))}
              <button className="text-left px-4 py-2.5 text-sm font-semibold rounded-lg transition-all hover:bg-white/5 hover:text-white" style={{ color: "#9CA3AF" }} onClick={() => { navigate("/login"); setMobileMenu(false); }}>Log In</button>
              <button className="text-left px-4 py-2.5 text-sm font-semibold rounded-lg transition-all hover:bg-white/5" style={{ color: "#EC4899" }} onClick={() => { navigate("/register"); setMobileMenu(false); }}>Get Started Free</button>
            </div>
          </div>
        )}
      </nav>

      {/* ═══ HERO — Clean centered: Logo + Tagline + CTA only ═══ */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <Blobs v={1} />
        <NeuralBg />
        <CircuitTraces />
        <WaveLines />

        {/* Orbit rings (subtle, bg-blended) */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 hidden lg:block" style={{ width: 400, height: 400 }}>
          <div className="w-full h-full rounded-full" style={{ border: "1px solid rgba(139,92,246,0.06)" }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 orbit" style={{ width: 400, height: 400 }}>
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: "#8B5CF6", boxShadow: "0 0 10px rgba(139,92,246,0.5)" }} />
          </div>
        </div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 hidden lg:block" style={{ width: 580, height: 580 }}>
          <div className="w-full h-full rounded-full" style={{ border: "1px solid rgba(236,72,153,0.04)" }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 orbit2" style={{ width: 580, height: 580 }}>
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: "#EC4899", boxShadow: "0 0 8px rgba(236,72,153,0.4)" }} />
          </div>
        </div>

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          {/* Logo with glow */}
          <div className="relative inline-block mb-6">
            <div className="absolute inset-0 rounded-2xl spin-slow opacity-20" style={{ border: "1px dashed rgba(139,92,246,0.4)", margin: "-14px", borderRadius: "24px" }} />
            <div className="w-36 h-36 md:w-44 md:h-44 rounded-2xl relative z-10 flex items-center justify-center p-4" style={{ boxShadow: "0 0 60px rgba(139,92,246,0.2), 0 0 120px rgba(236,72,153,0.08)" }}>
              <img src={LOGO} alt="AIxTribe" className="w-full h-full object-contain rounded-xl" style={{ mixBlendMode: "screen" }} />
            </div>
          </div>

          {/* Tagline badge */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full glass">
              <Sparkles className="w-4 h-4 text-violet-400" />
              <span className="text-sm font-bold tracking-wide bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">Community for Tech People</span>
            </div>
          </div>

          {/* Main heading */}
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black tracking-tight leading-[1.1] mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
            <span className="text-white">your ai tribe</span><br />
            <span className="bg-gradient-to-r from-yellow-400 via-orange-400 via-pink-500 to-violet-500 bg-clip-text text-transparent">is waiting</span>
          </h1>

          {/* Description */}
          <p className="text-base sm:text-lg max-w-2xl mx-auto mb-10 leading-relaxed" style={{ color: "#9CA3AF" }}>
            23+ AI tools. Webinars. Jobs. Courses. The place where AI builders, dreamers & creators hang out. All free for 6 months.
          </p>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <button className="h-14 px-12 rounded-full font-bold text-lg text-white transition-all hover:-translate-y-1" style={{ background: "linear-gradient(135deg, #EC4899, #8B5CF6, #3B82F6)", boxShadow: "0 8px 40px rgba(139,92,246,0.35)" }} onClick={() => navigate("/register")} data-testid="hero-cta">
              Join Free Now <ArrowRight className="inline ml-2 w-5 h-5" />
            </button>
            <button className="h-14 px-8 rounded-full font-bold text-base text-cyan-300 transition-all hover:-translate-y-1 inline-flex items-center gap-2" style={{ background: "rgba(34,211,238,0.08)", border: "1px solid rgba(34,211,238,0.3)" }} onClick={() => startDemoTour()} data-testid="hero-watch-demo">
              <PlayCircle className="w-5 h-5" /> Watch Demo
            </button>
          </div>

          {/* Subtle stats */}
          <div className="mt-14 mb-16 flex flex-wrap justify-center gap-4">
            {[{v:"10K+",l:"Members",c:"#8B5CF6"},{v:"23+",l:"AI Tools",c:"#EC4899"},{v:"500+",l:"Webinars",c:"#3B82F6"},{v:"1K+",l:"Jobs",c:"#14B8A6"}].map((s,i) => (
              <div key={i} className="px-5 py-2.5 rounded-2xl glass transition-all hover:-translate-y-1">
                <span className="text-lg font-bold" style={{ color: s.c }}>{s.v}</span>
                <span className="text-xs ml-2 font-medium" style={{ color: "#9CA3AF" }}>{s.l}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ BIG FEATURE SCROLLER — Discord style ═══ */}
      <div className="py-3 md:py-4 relative overflow-hidden" style={{ background: "linear-gradient(90deg, #8B5CF6, #EC4899, #F59E0B)", borderTop: "2px solid rgba(255,255,255,0.15)", borderBottom: "2px solid rgba(255,255,255,0.15)" }}>
        <Marquee speed={50} gradient={false} pauseOnHover>
          {["post","message","events","webinars","hackathons","communities","ai news","ai workspace"].map((text,i) => (
            <span key={i} className="flex items-center">
              <span className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-white mx-4" style={{ fontFamily: "'Outfit', sans-serif" }}>{text}</span>
              <img src="/brain-icon.png" alt="" className="w-10 h-10 md:w-12 md:h-12 mx-4 object-contain" />
            </span>
          ))}
        </Marquee>
      </div>

      {/* ═══ CREATE A POST SECTION ═══ */}
      <section id="features" className="py-24 md:py-36 relative overflow-hidden">
        <Blobs v={2} />
        <NeuralBg />
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 items-center gap-16 lg:gap-24 relative z-10">
          <div>
            <span className="text-xs font-bold tracking-[0.2em] uppercase mb-4 block text-pink-400">SOCIAL FEED</span>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-tight mb-6">share your <span className="bg-gradient-to-r from-pink-400 to-amber-400 bg-clip-text text-transparent">wins</span></h2>
            <p className="text-lg font-medium leading-relaxed mb-6" style={{ color: "#9CA3AF" }}>Create posts with rich text, images, polls & hashtags. Get reactions, comments, and build your AI reputation.</p>
            <div className="flex flex-wrap gap-3">{["Rich Text Posts","Image Upload","Polls","Hashtag Trends","Nested Comments"].map((t,i) => <span key={i} className="px-4 py-2 rounded-full text-xs font-bold glass">{t}</span>)}</div>
          </div>
          <div className="relative"><PostMockup /></div>
        </div>
      </section>

      {/* ═══ MESSAGING SECTION ═══ */}
      <section className="py-24 md:py-36 relative overflow-hidden">
        <Blobs v={1} />
        <CircuitTraces />
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 items-center gap-16 lg:gap-24 relative z-10">
          <div className="order-2 lg:order-1 relative"><MessageMockup /></div>
          <div className="order-1 lg:order-2">
            <span className="text-xs font-bold tracking-[0.2em] uppercase mb-4 block text-emerald-400">MESSAGING</span>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-tight mb-6">connect <span className="bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">instantly</span></h2>
            <p className="text-lg font-medium leading-relaxed mb-6" style={{ color: "#9CA3AF" }}>Real-time messaging with reactions, threads, voice and video calls. Collaborate on projects and build together.</p>
            <div className="flex flex-wrap gap-3">{["Real-time Chat","Reactions","Voice Calls","Video Calls","File Sharing"].map((t,i) => <span key={i} className="px-4 py-2 rounded-full text-xs font-bold glass">{t}</span>)}</div>
          </div>
        </div>
      </section>

      {/* ═══ AI TOOLS SCROLLER ═══ */}
      <div className="py-3 md:py-4" style={{ background: "linear-gradient(90deg, #8B5CF6, #EC4899, #F59E0B)", borderTop: "2px solid rgba(255,255,255,0.15)", borderBottom: "2px solid rgba(255,255,255,0.15)" }}>
        <Marquee speed={50} direction="right" gradient={false} pauseOnHover>
          {["pitch deck","ai chat","image gen","code assist","resume builder","email composer","translator","voice ai","document ai","claude"].map((w,i) => (
            <span key={i} className="flex items-center mx-6">
              <span className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-white">{w}</span>
              <img src="/brain-icon.png" alt="" className="w-8 h-8 md:w-10 md:h-10 mx-6 object-contain" />
            </span>
          ))}
        </Marquee>
      </div>

      {/* ═══ AI TOOLS SECTION ═══ */}
      <section id="tools" className="py-24 md:py-36 relative overflow-hidden">
        <Blobs v={2} />
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 items-center gap-16 lg:gap-24 relative z-10">
          <div>
            <span className="text-xs font-bold tracking-[0.2em] uppercase mb-4 block text-emerald-400">AI WORKSPACE</span>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-tight mb-6">23+ tools <span className="bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">one hub</span></h2>
            <p className="text-lg font-medium leading-relaxed mb-8" style={{ color: "#9CA3AF" }}>GPT-4o, Claude, Gemini, Whisper — all integrated. Generate, create, code, compose.</p>
            <button className="h-12 px-8 rounded-full font-bold text-white transition-all hover:-translate-y-1" style={{ background: "linear-gradient(135deg, #8B5CF6, #EC4899)" }} onClick={() => navigate("/register")}>Try AI Tools <ArrowRight className="inline ml-2 w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {[{icon:Bot,name:"AI Chat",c:"#0EA5E9"},{icon:Bot,name:"Claude",c:"#D97706"},{icon:Mail,name:"Email AI",c:"#3B82F6"},{icon:Image,name:"Image Gen",c:"#EC4899"},{icon:Code,name:"Code",c:"#22C55E"},{icon:FileText,name:"Summarize",c:"#14B8A6"},{icon:FileText,name:"Resume",c:"#60A5FA"},{icon:Wand2,name:"Prompt",c:"#FBBF24"},{icon:Mic,name:"Voice",c:"#F472B6"}].map((t,i) => (
              <div key={i} className="rounded-2xl p-4 text-center transition-all hover:-translate-y-1 cursor-pointer group glass-strong">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-2" style={{ backgroundColor: `${t.c}15` }}><t.icon className="w-5 h-5 group-hover:scale-110 transition-transform" style={{ color: t.c }} /></div>
                <p className="text-white text-xs font-bold">{t.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ EVENTS ═══ */}
      <section id="events" className="py-24 md:py-36 relative overflow-hidden">
        <Blobs v={1} />
        <NeuralBg />
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 items-center gap-16 lg:gap-24 relative z-10">
          <div>
            <span className="text-xs font-bold tracking-[0.2em] uppercase mb-4 block text-amber-400">EVENTS & WEBINARS</span>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-tight mb-6">host. learn. <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">earn.</span></h2>
            <p className="text-lg font-medium leading-relaxed mb-8" style={{ color: "#9CA3AF" }}>Monetized webinars with payment walls. Hackathons with teams. AI-powered video interviews. Events with RSVP.</p>
            <button className="h-12 px-8 rounded-full font-bold text-white transition-all hover:-translate-y-1" style={{ background: "linear-gradient(135deg, #F59E0B, #EF4444)" }} onClick={() => navigate("/register")}>Start Hosting <ArrowRight className="inline ml-2 w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[{icon:Play,l:"Live Webinars",c:"#A855F7"},{icon:Award,l:"Hackathons",c:"#FBBF24"},{icon:Calendar,l:"Events",c:"#0EA5E9"},{icon:Video,l:"Video Interview",c:"#22C55E"},{icon:Target,l:"Ad Manager",c:"#EC4899"},{icon:Briefcase,l:"Job Board",c:"#F97316"}].map((c,i) => (
              <div key={i} className="rounded-2xl p-5 transition-all hover:-translate-y-2 glass-strong">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${c.c}15` }}><c.icon className="w-6 h-6" style={{ color: c.c }} /></div>
                <p className="text-white text-sm font-bold">{c.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ WHY US ═══ */}
      <section className="py-24 md:py-36 text-center relative overflow-hidden">
        <Blobs v={2} />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-tight mb-5">why <span className="bg-gradient-to-r from-amber-400 to-pink-400 bg-clip-text text-transparent">10,000+</span> choose us</h2>
          <p className="text-lg font-medium mb-16" style={{ color: "#6B7280" }}>not just a social network. a launchpad.</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[{icon:Zap,title:"AI-First",desc:"Every feature powered by AI.",c:"#8B5CF6"},{icon:Shield,title:"Secure",desc:"2FA, moderation, admin controls.",c:"#22C55E"},{icon:Rocket,title:"Career",desc:"Jobs, portfolio, verification.",c:"#FBBF24"},{icon:Heart,title:"Earn",desc:"Monetize webinars, courses, ads.",c:"#EC4899"}].map((b,i) => (
              <div key={i} className="rounded-3xl p-8 text-center transition-all hover:-translate-y-2 glass-strong">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{ backgroundColor: `${b.c}15` }}><b.icon className="w-7 h-7" style={{ color: b.c }} /></div>
                <h3 className="text-white font-bold text-lg mb-2">{b.title}</h3>
                <p className="text-sm font-medium leading-relaxed" style={{ color: "#6B7280" }}>{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FREE BANNER ═══ */}
      <div className="py-4" style={{ background: "linear-gradient(90deg, #8B5CF6, #EC4899, #F59E0B)" }}>
        <p className="text-center text-sm font-black tracking-wider text-white">ALL PAID SERVICES FREE FOR 6 MONTHS FROM REGISTRATION</p>
      </div>

      {/* ═══ CTA ═══ */}
      <section className="py-24 md:py-40 relative overflow-hidden">
        <Blobs v={1} />
        <NeuralBg />
        <WaveLines />
        <div className="max-w-4xl mx-auto text-center relative z-10 px-6">
          <h2 className="text-4xl sm:text-5xl lg:text-7xl font-black tracking-tighter leading-[0.95] mb-8"><span className="text-white">ready to join</span><br /><span className="bg-gradient-to-r from-violet-400 via-pink-400 to-amber-400 bg-clip-text text-transparent">the tribe?</span></h2>
          <p className="text-xl font-medium mb-10" style={{ color: "#9CA3AF" }}>Create your free account. Start exploring. Level up.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="h-16 px-12 rounded-full font-bold text-xl text-white transition-all hover:-translate-y-1" style={{ background: "linear-gradient(135deg, #8B5CF6, #EC4899)", boxShadow: "0 8px 40px rgba(139,92,246,0.3)" }} onClick={() => navigate("/register")}>Join Free Now <ArrowRight className="inline ml-2 w-6 h-6" /></button>
            <button className="h-16 px-10 rounded-full font-bold text-xl text-cyan-300 transition-all hover:-translate-y-1 inline-flex items-center justify-center gap-2" style={{ background: "rgba(34,211,238,0.08)", border: "1px solid rgba(34,211,238,0.3)" }} onClick={() => startDemoTour()} data-testid="cta-watch-demo">
              <PlayCircle className="w-5 h-5" /> Watch Demo
            </button>
            <button className="h-16 px-10 rounded-full font-bold text-xl transition-all hover:-translate-y-1 glass" onClick={() => navigate("/login")}>Log In</button>
          </div>
        </div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer id="contact" style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          <div className="grid md:grid-cols-4 gap-10">
            <div><div className="mb-4"><div className="w-36 h-36 md:w-44 md:h-44 rounded-xl flex items-center justify-center p-4"><img src={LOGO} alt="AIxTribe" className="w-full h-full object-contain rounded-lg" /></div></div><p className="text-sm font-medium leading-relaxed" style={{ color: "#6B7280" }}>India's first AI community platform.</p></div>
            {[
              { title: "Explore", items: [
                { label: "Events", to: "/events" },
                { label: "Webinars", to: "/webinars" },
                { label: "Hackathons", to: "/hackathons" },
                { label: "AI News", to: "/ai-news" },
                { label: "AI Quiz Arena", to: "/ai-quiz" },
              ]},
              { title: "Learn & Earn", items: [
                { label: "AI Courses", to: "/ai-courses" },
                { label: "AI Jobs", to: "/ai-jobs" },
                { label: "AI Tools", to: "/ai-tools" },
                { label: "Blog", to: "/blog" },
              ]},
            ].map((col,i) => (
              <div key={i}><h4 className="text-xs font-bold tracking-[0.2em] text-violet-400 mb-4">{col.title.toUpperCase()}</h4><ul className="space-y-2.5">{col.items.map(item => <li key={item.label}><button onClick={() => navigate(item.to)} className="text-sm font-medium transition-colors hover:text-white" style={{ color: "#6B7280" }}>{item.label}</button></li>)}</ul></div>
            ))}
            <div><h4 className="text-xs font-bold tracking-[0.2em] text-violet-400 mb-4">CONTACT</h4><ul className="space-y-2.5 text-sm" style={{ color: "#6B7280" }}><li className="flex items-center gap-2"><Mail className="w-4 h-4 text-violet-400" /> info@aixtribe.com</li><li className="flex items-center gap-2"><Globe className="w-4 h-4 text-violet-400" /> www.aixtribe.com</li></ul>
              {socialLinks.length > 0 && (
                <div className="flex flex-wrap gap-2.5 mt-5" data-testid="gv2-footer-social-links">
                  {socialLinks.map(link => {
                    const SIcon = SOCIAL_ICON_MAP[link.platform?.toLowerCase()] || ExternalLink;
                    return (
                      <a key={link.id} href={link.url} target="_blank" rel="noopener noreferrer"
                        className="w-9 h-9 flex items-center justify-center rounded-lg transition-all group"
                        style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.08)" }}
                        onMouseEnter={e => { e.currentTarget.style.background = "rgba(139,92,246,0.15)"; e.currentTarget.style.borderColor = "rgba(139,92,246,0.4)"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.05)"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; }}
                        title={link.label || link.platform} data-testid={`gv2-social-${link.platform?.toLowerCase()}`}>
                        <SIcon className="w-4 h-4" style={{ color: "#9CA3AF" }} />
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="py-5 px-6" style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 mb-3">
            <button onClick={() => navigate("/privacy-policy")} className="text-xs font-medium transition-colors hover:text-violet-400" style={{ color: "#6B7280" }} data-testid="footer-privacy">Privacy Policy</button>
            <button onClick={() => navigate("/terms")} className="text-xs font-medium transition-colors hover:text-violet-400" style={{ color: "#6B7280" }} data-testid="footer-terms">Terms & Conditions</button>
            <button onClick={() => navigate("/blog")} className="text-xs font-medium transition-colors hover:text-violet-400" style={{ color: "#6B7280" }} data-testid="footer-blog">Blog</button>
            <button onClick={() => navigate("/faq")} className="text-xs font-medium transition-colors hover:text-violet-400" style={{ color: "#6B7280" }} data-testid="footer-faq">FAQ</button>
          </div>
          <p className="text-center text-xs" style={{ color: "#4B5563" }}>&copy; 2026 AIxTribe. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default ThemeGlassV2;
